import { cn } from '~/common/utils/styleUtil';
import { SearchIcon, XIcon } from 'lucide-react';
import { Form, useSubmit } from 'react-router';
import type { ChangeEvent} from 'react';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBasePath } from '~/common/hooks/useBasePath';
import { QuickSearchFilter } from '~/features/catalog/components/quicksearch/QuickSearchFilter';
import {
  QUERY_SEARCH_PARAM_SEARCHTERM
} from '~/features/catalog/mappers/catalogSearchQueryMapper';

export interface QuickSearchInputProps {
  className?: string;
  onFocus?: () => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  searchTerm: string;
  onClear?: () => void;
  onSubmit?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onSearchButtonClick?: () => void;
}

export const QuickSearchInput = forwardRef<HTMLInputElement, Readonly<QuickSearchInputProps>>(
  ({ onKeyDown, onFocus, onChange, className, searchTerm, onClear, onSubmit, onSearchButtonClick }, ref) => {
    const { t } = useTranslation();
    const { path } = useBasePath();
    const submit = useSubmit();

    const handleFocus = () => {
      onFocus?.();
    };

    const handleSearchButtonClick = () => {
      onSearchButtonClick?.();
    };

    return (
      <Form
        className={cn(
          'flex bg-white items-center w-full rounded-md border border-input px-3 py-1 text-base shadow-xs transition-colors placeholder:text-muted-foreground focus-within:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        onSubmit={(event: React.SubmitEvent<HTMLFormElement>) => {
          event.preventDefault();
          const formData = new FormData(event.currentTarget);

          if (formData.has(QUERY_SEARCH_PARAM_SEARCHTERM)) {
            const searchTerm = (formData.get(QUERY_SEARCH_PARAM_SEARCHTERM) as string)?.trim();

            if (searchTerm?.length === 0) {
              formData.delete(QUERY_SEARCH_PARAM_SEARCHTERM);
            } else {
              formData.set(QUERY_SEARCH_PARAM_SEARCHTERM, searchTerm);
            }
          }
          submit(formData, { action: `${path}/catalog` });

          if (onSubmit) {
            onSubmit();
          }
        }}
      >
        <input
          onKeyDown={onKeyDown}
          ref={ref}
          value={searchTerm}
          type="text"
          name="searchTerm"
          autoComplete="off"
          placeholder={t('Suche nach Produkten und Marken')}
          className="w-full p-2 px-2 outline-hidden rounded-md border-none"
          data-testid={'quicksearch-input'}
          onChange={event => {
            onChange?.(event);
          }}
          onFocus={handleFocus}
        />
        {searchTerm?.length > 0 && (
          <button
            type={'button'}
            className="px-2 hover:text-primary"
            onClick={onClear}
            data-testid={'quicksearch-clear-button'}
          >
            <XIcon />
          </button>
        )}
        <button
          type="button"
          className="px-2 hover:text-primary"
          data-testid={'quicksearch-button'}
          onClick={handleSearchButtonClick}
        >
          <SearchIcon />
        </button>
        <QuickSearchFilter />
      </Form>
    );
  }
);

QuickSearchInput.displayName = 'QuickSearchInput';
